import { APP_DRAWER_ELEMENT_ID, MODAL_CONTENT_ID_PREFIX, MODAL_ELEMENT_ID_PREFIX } from "Common/componentUtils/constants";
import Close from "Components/close";
import withOverlaysProvider from "Components/overlaysContext/withOverlaysProvider";
import ProgressOverlay from "Components/progressOverlay";
import { themeVariables } from "Components/theme";
import classNames from "classnames";
import { Popup } from "devextreme-react/popup";
import join from "lodash/join";
import map from "lodash/map";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import locale from "./withModal.locale";
import Styled, { SIZES } from "./withModal.styled";
const notReadonlySubQuery = ":not([readonly])";
const focusableInputSelector = join(map(["input[type=text]", "input[type=password]", "textarea"], selector => `${selector}${notReadonlySubQuery}`), ", ");
function WithModal(props) {
    const popupRef = useRef();
    const contentContainerRef = useRef(null);
    const modalId = useMemo(() => `${MODAL_ELEMENT_ID_PREFIX}${uuid()}`, []);
    const modalContentId = useMemo(() => `${MODAL_CONTENT_ID_PREFIX}${uuid()}`, []);
    const [inAnimationProgress, setInAnimationProgress] = useState(false);
    const rafId = useRef(0);
    useEffect(() => {
        if (props.popupRef && popupRef.current) {
            props.popupRef.current = popupRef.current;
        }
        return () => {
            props.onHide && props.onHide();
        };
    }, []);
    useEffect(() => {
        rafId.current && cancelAnimationFrame(rafId.current);
        rafId.current = requestAnimationFrame(() => {
            var _a;
            if (!((_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.instance))
                return;
            popupRef.current.instance.option({
                height: props.defaultHeight,
                width: props.defaultWidth
            });
            contentContainerRef.current && contentContainerRef.current.style.setProperty("opacity", "1");
        });
        return () => {
            contentContainerRef.current && contentContainerRef.current.style.setProperty("opacity", "0");
        };
    }, [props.title, props.defaultHeight, props.defaultWidth]);
    const handleHidden = useCallback((e) => {
        props.onHide && props.onHide(e);
        setInAnimationProgress(false);
    }, [props.onHide]);
    const handleHiding = useCallback(() => {
        setInAnimationProgress(true);
    }, []);
    const handleShown = useCallback((e) => {
        var _a;
        const inputs = (_a = document
            .getElementById(modalId)) === null || _a === void 0 ? void 0 : _a.querySelectorAll(focusableInputSelector);
        inputs && inputs[0] && inputs[0].focus();
        props.onShow && props.onShow(e);
        window.dispatchEvent(new Event("resize"));
        props.resizeEnabled && e.component.option({ height: undefined, width: undefined });
        setInAnimationProgress(false);
    }, [props.onShow, props.resizeEnabled, modalId]);
    const handleShowing = useCallback((e) => {
        props.resizeEnabled &&
            e.component.option({ height: props.defaultHeight, width: props.defaultWidth });
        setInAnimationProgress(true);
    }, [props.resizeEnabled, props.defaultHeight, props.defaultWidth]);
    const handleResizeEnd = useCallback(() => {
        window.dispatchEvent(new Event("resize"));
    }, []);
    const FormContainerComp = useMemo(() => withOverlaysProvider(ProgressOverlay, modalId), []);
    const modalHeader = useMemo(() => (React.createElement("div", { className: classNames(props.headerOutsideModal && "w-100-max position-relative", !props.headerOutsideModal && "caption-with-actions-panel", !props.headerOutsideModal &&
            (props.headerAlignment === "center" ? "v-center-items" : "v-baseline-items")) },
        React.createElement("div", { className: classNames("text-overflow-ellipsis", props.headerOutsideModal && "w-100-max text-center nowrap", props.headerClassName) }, props.title),
        props.closeBtn &&
            (!props.headerOutsideModal ? (React.createElement(Close, { fontSize: SIZES.CLOSE_FONT, size: SIZES.CLOSE_ICON_SIZE, title: locale.CLOSE, animateHoverColor: true, onClick: () => {
                    props.onHide && props.onHide();
                } })) : (React.createElement(Close, { fontSize: SIZES.CLOSE_FONT, size: SIZES.CLOSE_BTN_SIZE, title: locale.CLOSE, animateHoverColor: true, onClick: () => {
                    props.onHide && props.onHide();
                }, btnMode: true, btnModePadding: SIZES.CLOSE_BTN_PADDING, btnRounded: false, className: "position-absolute text-gray-500 shadow-darker", style: { right: SIZES.CLOSE_BTN_OFFSET_RIGHT, top: SIZES.CLOSE_BTN_OFFSET_TOP } }))))), [
        props.title,
        props.headerOutsideModal,
        props.headerClassName,
        props.headerAlignment,
        props.onHide
    ]);
    const wrapperAttr = useMemo(() => ({
        id: modalId,
        class: classNames(props.className, props.headerOutsideModal && "header-outside-of-modal", "modal"),
        zIndex: props.zIndex,
        ...(props.wrapperAttr || {})
    }), [props.className, props.wrapperAttr, props.zIndex, modalId, props.headerOutsideModal]);
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === "Escape" && props.show) {
                props.onHide && props.onHide();
            }
        };
        if (props.show) {
            document.addEventListener("keydown", handleKeyPress);
        }
        return () => {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [props.show, props.onHide]);
    const defaultContentRender = useCallback(() => (React.createElement(Styled.Container, { id: modalContentId, ref: contentContainerRef, rowsCount: props.footer ? 2 : 1, className: classNames(inAnimationProgress && "no-pointer-events") },
        React.createElement(FormContainerComp, { className: classNames("h-100-max", props.contentClassName, props.contentOverflowHidden && "overflow-hidden"), show: props.isLoading, ignoreParentOverlayInProgress: true, zIndex: props.zIndex ? props.zIndex + 1 : undefined }, props.children),
        props.footer && (React.createElement("div", { className: classNames("modal-footer-padding", props.isLoading && "disabled-area") }, props.footer)))), [
        modalContentId,
        props.children,
        props.footer,
        props.isLoading,
        props.contentClassName,
        props.contentOverflowHidden,
        props.zIndex,
        inAnimationProgress
    ]);
    return (React.createElement(Popup, { animation: props.animation, ref: popupRef, visible: props.show, defaultVisible: true, container: props.container, onHidden: handleHidden, onHiding: handleHiding, onShown: handleShown, onShowing: handleShowing, wrapperAttr: wrapperAttr, hideOnOutsideClick: props.hideOnOutsideClick, hideOnParentScroll: false, showCloseButton: false, deferRendering: false, dragEnabled: props.dragEnabled, resizeEnabled: props.resizeEnabled, focusStateEnabled: false, shading: true, shadingColor: props.shadingColor, width: !props.resizeEnabled ? props.width : undefined, maxWidth: props.maxWidth, defaultWidth: props.defaultWidth, height: !props.resizeEnabled ? props.height : undefined, maxHeight: props.maxHeight, defaultHeight: props.defaultHeight, titleRender: () => modalHeader, showTitle: props.showTitle, position: props.position, onResizeEnd: handleResizeEnd, contentRender: props.contentRender || defaultContentRender }));
}
WithModal.defaultProps = {
    height: "inherit",
    maxHeight: `calc(100vh - ${parseInt(themeVariables["panel-body-padding"]) * 2}px)`,
    maxWidth: `calc(100vw - ${parseInt(themeVariables["panel-body-padding"]) * 2}px)`,
    shadingColor: "rgba(0, 0, 0, 0.5)",
    headerClassName: "font-h5 font-bold",
    headerAlignment: "center",
    showTitle: true,
    closeBtn: true,
    hideOnOutsideClick: false,
    animation: {
        show: {
            type: "pop",
            from: { opacity: 0, scale: 0 },
            to: { opacity: 1, scale: 1 },
            duration: 300
        },
        hide: {
            type: "pop",
            from: { opacity: 1, scale: 1 },
            to: { opacity: 0, scale: 0 },
            duration: 250
        }
    },
    container: `#${APP_DRAWER_ELEMENT_ID}`,
    dragEnabled: true,
    resizeEnabled: true,
    footerClassName: "border-top",
    contentOverflowHidden: true,
    zIndex: parseInt(themeVariables["zindex-devextreme-dx-menu-base"])
};
export default WithModal;
